/// <amd-module name="Reporting/BoardsCommon/BoardsReport" />
import { AnalyticsExceptionType, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { AccessDeniedMessage } from "Reporting/BoardsCommon/AccessDeniedMessage";
import { ReportError } from "Reporting/BoardsCommon/ReportError";
import { ReportLoading } from "Reporting/BoardsCommon/ReportLoading";
import { telemetryAreaName } from "Reporting/BoardsCommon/ReportTelemetry";
import * as Resources from "Reporting/BoardsCommon/Resources";
import { ReportWidgetHostContainer } from "Reporting/ReportWidgetHost/ReportWidgetHost";
import { VssComponent } from "VSS/Platform/Layout";
import { traceException } from "VSS/Platform/Trace";
import { IButtonProps } from "VSSUI/Button";
import { Header, TitleSize } from "VSSUI/Header";
import { css } from "VSSUI/Util";

export interface BoardsReportProps {
    teamId: string;
    teamName: string;
}

export interface BoardsReportState<WidgetSettings> {
    widgetSettings?: WidgetSettings;
    /**
     * Container for error state information
     */
    error?: any;
    knownWarning?: any;
}

/** Provides common handling for Widget-Driven Reports */
export abstract class BoardsReport<P extends BoardsReportProps, WidgetSettings, S extends BoardsReportState<WidgetSettings>> extends VssComponent<
    P,
    S
> {
    /** Variable for the getTeamContext method. Call that method. Do not use this variable directly. */
    private lastTeamContext: WidgetTeamContext | undefined;

    public abstract getWidgetContributionId(): string;
    public abstract getReportTitle(): string;
    public abstract getReportTelemetryName(): string;
    public abstract getFilterItems(): JSX.Element;
    public abstract renderTeachingBubble(): JSX.Element;
    public abstract decorateWidgetContent(widgetContent: JSX.Element | null): JSX.Element;
    /** This operation is performed every render cycle. Currently it is used to initialize teaching bubbles as they need to be refreshed every update-render cycle */
    public abstract initializeOnRender(): void;
    public abstract isConfigurationValid(): boolean;

    /**
     * Note: Each implementation should handle warnings which the report can generate.
     * If a warning component doesn't handle a warning, the warning will be treated as an error.
     */
    public abstract getKnownWarningComponent(): JSX.Element | null;

    public getWidgetName(): string {
        return this.getReportTitle();
    }

    public render(): JSX.Element | null {
        if (this.isMinimalReport()) {
            // minimal report is needed for preloading or header chart which forces widget content to load , thereby firing queries which can be cached for later use
            return this.renderMinimalReport();
        }

        this.initializeOnRender();

        const reportContents = this.isLightbox() ? this.renderLightboxReportContents() : this.renderReportContents();
        const reportClassName = this.isLightbox() ? "" : "bolt-page-grey";

        return (
            <div className={css("boards-widget-report", reportClassName)}>
                {this.getTitleBlock()}
                {reportContents}
                {!this.isReady() && !this.isInAbnormalState() && <ReportLoading />}
                {this.state.error && <ReportError errorMessage={this.state.error} />}
                {this.state.knownWarning && this.renderConfigurationWarning()}
            </div>
        );
    }
    public isReady(): boolean {
        return this.state.widgetSettings != null;
    }

    public getFilterRow(content: JSX.Element): JSX.Element {
        return (
            <div className="report-filter-area flex-self-center">
                <div className="report-filter-area-content flex-row">{content}</div>
            </div>
        );
    }

    /** Default implementation just renders the widget with the current settings if the widget is in a valid configuration. Render size uses 6 cells wide * 4 cells high, if this were rendered on a dashboard. */
    public getWidgetContent(): JSX.Element | null {
        if (!this.isConfigurationValid()) {
            return null;
        }

        return (
            <ReportWidgetHostContainer
                settings={JSON.stringify(this.state.widgetSettings)}
                name={this.getWidgetName()}
                parentPageName={this.getParentPagePrefix() + this.getReportTelemetryName()}
                contributionId={this.getWidgetContributionId()}
                renderSize={this.getSize()}
                widgetPinningSize={this.getWidgetPinningSize()}
                allowPinning={this.allowPinning()}
                teamContext={this.getTeamContext()}
                lastRefreshDate={this.getLastRefreshDate()}
            />
        );
    }

    public renderMinimalReport(): JSX.Element | null {
        if (!this.isReady() && !this.isInAbnormalState()) {
            return null;
        }
        if (this.isInAbnormalState()) {
            return this.renderConfigurationWarning();
        }
        return this.getWidgetContent();
    }

    public getTitleBlock(children?: JSX.Element | null): JSX.Element {
        return (
            <Header
                titleSize={TitleSize.Large}
                backButtonProps={this.getBackButtonProps()}
                title={
                    <>
                        {this.getReportTitle()} {children}
                    </>
                }
                titleClassName="boards-report-header-title"
            />
        );
    }

    public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({
            error: error.toString()
        });
        traceException(this.context.pageContext, telemetryAreaName, this.getReportTelemetryName(), error);
    }

    /** Describes if the report is in an terminal state where the normal widget content cannot be rendered */
    public isInAbnormalState(): boolean {
        return this.state.error != null || this.state.knownWarning != null;
    }

    // Return undefined except when derived report implements last refresh mechanism to force update chart by throwing away and creating new widget host
    public getLastRefreshDate(): Date | undefined {
        return undefined;
    }

    public isMinimalReport(): boolean | undefined {
        return false;
    }

    public isLightbox(): boolean | undefined {
        return false;
    }

    public getDefaultState(): BoardsReportState<WidgetSettings> {
        return {
            widgetSettings: undefined,
            error: undefined,
            knownWarning: undefined
        };
    }

    /** Runs extensibility point, but falls back to a hard error if no warning handler present.  */
    public renderConfigurationWarning(): JSX.Element | null {
        let warning;
        if (this.state.knownWarning === Resources.AccessDeniedSummary && !this.isMinimalReport()) {
            warning = <AccessDeniedMessage />;
        } else {
            warning = this.getKnownWarningComponent();
        }

        // @ts-ignore TypeScript 4.0 upgrade
        return warning !== null ? warning : <ReportError errorMessage={this.state.knownWarning} />;
    }

    protected handleCommonInitError(e: any) {
        if (recognizeAnalyticsException(e) === AnalyticsExceptionType.MissingAnalyticsPermissions) {
            this.setState({
                knownWarning: Resources.AccessDeniedSummary
            });
        } else {
            this.setState({
                error: e.toString()
            });
            traceException(this.context.pageContext, telemetryAreaName, this.getReportTelemetryName(), e);
        }
    }

    protected getBackButtonProps(): IButtonProps | undefined {
        return undefined;
    }

    // Allows for suppressing pinning of widgets with no config experience. This can be removed once Sprint Burndown ships.
    protected allowPinning(): boolean {
        return true;
    }

    protected getWidgetPinningSize() {
        return {
            columnSpan: 3,
            rowSpan: 2
        };
    }

    private renderReportContents(): JSX.Element | null {
        if (!this.isReady()) {
            return null;
        }
        if (this.isInAbnormalState()) {
            return this.renderConfigurationWarning();
        }
        return (
            <div className="boards-report-content">
                {this.getFilterRow(this.getFilterItems())}
                {this.decorateWidgetContent(this.getWidgetContent())}
                {this.renderTeachingBubble()}
            </div>
        );
    }

    private renderLightboxReportContents(): JSX.Element | null {
        if (!this.isReady()) {
            return null;
        }
        if (this.isInAbnormalState()) {
            return this.renderConfigurationWarning();
        }

        return (
            <div className="boards-report-lightbox-content">
                {this.getWidgetContent()}
                {this.renderTeachingBubble()}
            </div>
        );
    }

    // Returns widget content size based on whether we are rendering the header chart or the full report/lightbox
    private getSize() {
        if (this.isMinimalReport()) {
            return {
                width: 140,
                height: 40
            };
        }
        if (this.isLightbox()) {
            return {
                width: 1010,
                height: 483
            };
        }
        return {
            width: 1010,
            height: 670
        };
    }

    private getParentPagePrefix(): string {
        if (this.isMinimalReport()) {
            return "Header";
        } else if (this.isLightbox()) {
            return "Lightbox";
        } else {
            return "";
        }
    }

    /** Constructs a team context object from the component props. */
    private getTeamContext = (): WidgetTeamContext => {
        if (this.lastTeamContext == null || this.lastTeamContext.id != this.props.teamId || this.lastTeamContext.name != this.props.teamName) {
            this.lastTeamContext = { id: this.props.teamId, name: this.props.teamName };
        }

        return this.lastTeamContext;
    };
}
